// ChecklistItemDrawer - the in-app view of one checklist item, opened from
// the Checklists page: the whole checklist (same group, or the project's
// items) with done / not-done checkboxes on the left, and on the right the
// selected item's party (partner / contractor + responsible person), the
// schedule activity it belongs to, the update thread (comments, statuses,
// screenshots, handoffs) and a post-update form - comment, status, attach
// files of any kind, take a photo / video, or paste (all optional; they
// go on the thread with the update, so completing an item with proof
// shows the proof in the Updates column), hand it to someone (they get
// the validate link by email) - plus "Send validate link" and the public link. Same logic as an
// issue, scoped to what the viewer may do (can.checklistEdit).
//
// Props: open, onClose, projectId, item, items (all project items), roster
//        ([{companyId, companyName, users:[{userId,name,email,contact}]}]),
//        planItems, can, onChanged (reload), onSelectItem (switch item),
//        onRemind (open the validate-link modal for an item)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { filterPeopleOption, renderPeopleOption } from "components/PeoplePicker/PeoplePicker";
import { peoplePickerOptions, flatOptions, groupLabel, userOrEmailRef, emailValue } from "components/PeoplePicker/peoplePickerOptions";
import { Button, Checkbox, Drawer, Input, Select, Space, Tag, Tooltip, Typography, message } from "antd";
import { BellOutlined, LinkOutlined, SendOutlined, EditOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import ChecklistMediaPicker, { ChecklistThreadMedia } from "components/Checklist/ChecklistMediaPicker";
import ActivityPicker from "components/Coordination/ActivityPicker";
import { activityLabel, activityTitle } from "helpers/schedule/activityLabel";
import { matchOption } from "components/SearchSelect/searchSelect";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });
const api = async (path, options = {}) => {
  const res = await fetch(`${url}${path}`, { ...options, headers: { "Content-Type": "application/json", ...auth(), ...(options.headers || {}) } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
  return body;
};
const STATUS_COLOR = { open: "default", in_progress: "blue", pending_review: "gold", complete: "green", abandoned: "default" };
const STATUS_LABEL = { open: "Open", in_progress: "In progress", pending_review: "Awaiting approval", complete: "Complete", abandoned: "Abandoned" };
const fmt = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export default function ChecklistItemDrawer({ open, onClose, projectId, item, items = [], roster = [], planItems = [], can = {}, onChanged, onSelectItem, onRemind, onEdit }) {
  const canEdit = !!can.checklistEdit;
  const [status, setStatus] = useState("in_progress");
  const [comment, setComment] = useState("");
  // Files attached for the next update - optional, any kind; an image
  // among them satisfies "require screenshot".
  const [media, setMedia] = useState([]); // [{url,name,kind,mime,size}]
  const [assignTo, setAssignTo] = useState(undefined); // email
  const [posting, setPosting] = useState(false);
  const [filter, setFilter] = useState("");
  const threadEndRef = useRef(null);

  useEffect(() => { setComment(""); setMedia([]); setAssignTo(undefined); setStatus(item?.status === "complete" ? "complete" : "in_progress"); }, [item?._id]);
  useEffect(() => { if (threadEndRef.current) threadEndRef.current.scrollIntoView({ block: "end" }); }, [item?.thread?.length]);

  // The checklist shown on the left: the item's group when it has one
  // (imported sheets / sections), else every item of the project.
  const listItems = useMemo(() => {
    const sameGroup = item?.group ? items.filter((i) => i.group === item.group) : [];
    const base = sameGroup.length > 1 ? sameGroup : items;
    const q = filter.trim().toLowerCase();
    return q ? base.filter((i) => String(i.title || "").toLowerCase().includes(q)) : base;
  }, [items, item, filter]);
  const doneCount = listItems.filter((i) => i.status === "complete").length;

  // The shared picker's options (components/PeoplePicker, owner 2026-10-15):
  // "company - trade" labels, people tagged by kind. A partner executive is
  // greyed out as the responsible person (a reader); the hand-off below
  // only sends him the link, so there he is offered.
  const companyOptions = roster.map((g) => ({ value: g.companyId, label: groupLabel(g) }));
  const peopleOf = (companyId) => {
    const g = roster.find((x) => String(x.companyId) === String(companyId));
    return flatOptions(peoplePickerOptions(g ? [g] : [], { personValue: userOrEmailRef, executives: "disable" }))
      .map((o) => ({ ...o, name: (o.person && o.person.name) || o.label }));
  };
  const everyone = useMemo(
    () => peoplePickerOptions(roster, { personValue: emailValue })
      .map((g) => ({ ...g, options: g.options.map((o) => ({ ...o, name: (o.person && o.person.name) || o.label })) })),
    [roster]
  );

  const patch = async (body) => {
    try { await api(`/projects/${projectId}/checklist/${item._id}`, { method: "PATCH", body: JSON.stringify(body) }); onChanged && onChanged(); }
    catch (e) { message.error(e.message); }
  };
  const toggle = async (row, checked) => {
    try { await api(`/projects/${projectId}/checklist/${row._id}`, { method: "PATCH", body: JSON.stringify({ status: checked ? "complete" : "open" }) }); onChanged && onChanged(); }
    catch (e) { message.error(e.message); }
  };
  const uploadMedia = async (files) => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f, f.name || "capture");
    const res = await fetch(`${url}/projects/${projectId}/checklist/${item._id}/media`, { method: "POST", headers: auth(), body: fd });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
    return body.added || [];
  };

  const needsShot = !!(item?.evidencePolicy && item.evidencePolicy.screenshot);
  const isImage = (a) => a && (a.kind === "image" || /^image\//.test(a.mime || ""));
  const hasAnyShot = media.some(isImage) || (item?.screenshots || []).length > 0 || (item?.attachments || []).some(isImage)
    || (item?.thread || []).some((t) => t.screenshotUrl || (t.attachments || []).some(isImage));
  const post = async () => {
    if (!comment.trim() && !media.length && !assignTo && status === (item?.status === "complete" ? "complete" : "in_progress")) return message.info("Write a comment, attach files, pick a status or hand it to someone");
    if (status === "complete" && needsShot && !hasAnyShot) return message.warning("This item requires a screenshot of the work before it can be completed - attach or paste one first");
    setPosting(true);
    try {
      const who = everyone.find((p) => p.value === String(assignTo || "").toLowerCase());
      await api(`/projects/${projectId}/checklist/${item._id}/update`, {
        method: "POST",
        body: JSON.stringify({
          status: status === "open" ? "pending" : status,
          comment: comment.trim(),
          screenshotUrl: (media.find(isImage) || {}).url || "",
          attachments: media,
          assignTo: assignTo ? { email: String(assignTo).toLowerCase(), name: who?.name || String(assignTo) } : null,
        }),
      });
      setComment(""); setMedia([]); setAssignTo(undefined);
      message.success(assignTo ? "Update posted and handed over by email" : "Update posted");
      onChanged && onChanged();
    } catch (e) { message.error(e.message); }
    finally { setPosting(false); }
  };

  if (!item) return null;
  const company = roster.find((g) => String(g.companyId) === String(item.responsibleCompanyId));
  const requireShotToggle = canEdit ? (
    <Checkbox style={{ fontSize: 12 }} checked={needsShot} onChange={(e) => patch({ requireScreenshot: e.target.checked })}>Require screenshot to complete</Checkbox>
  ) : (needsShot ? <Tag color="orange">Screenshot required</Tag> : null);
  const publicLink = `${window.location.origin}/checklist/validate/${item.token}`;

  return (
    <Drawer open={open} onClose={onClose} width={Math.min(1100, window.innerWidth - 40)} destroyOnClose
      title={<Space wrap><span>{item.title}</span><Tag color={STATUS_COLOR[item.status] || "default"}>{STATUS_LABEL[item.status] || item.status}</Tag>{item.group && <Tag>{item.group}</Tag>}</Space>}
      extra={<Space>
        {requireShotToggle}
        {canEdit && onEdit && <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(item)}>Edit</Button>}
        {canEdit && <Button size="small" icon={<BellOutlined />} onClick={() => onRemind && onRemind(item)}>Send validate link</Button>}
        <Tooltip title="Copy the public link (no login needed)"><Button size="small" icon={<LinkOutlined />} onClick={() => { if (navigator.clipboard) navigator.clipboard.writeText(publicLink); message.success("Link copied"); }}>Public link</Button></Tooltip>
      </Space>}>
      <div style={{ display: "flex", gap: 16, minHeight: "100%" }}>
        {/* Left: the checklist */}
        <div style={{ width: 340, flex: "0 0 340px", borderRight: "1px solid #f0f0f0", paddingRight: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <b style={{ fontSize: 13 }}>{listItems.length && item.group && listItems.every((i) => i.group === item.group) ? `Checklist · ${item.group}` : "Checklist"}</b>
            <span style={{ fontSize: 12, color: "#888" }}>{doneCount} / {listItems.length} done</span>
          </div>
          <Input.Search size="small" allowClear placeholder="Filter items…" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ marginBottom: 6 }} />
          <div style={{ maxHeight: "calc(var(--vh) * 100 - 220px)", overflowY: "auto" }}>
            {listItems.map((i) => (
              <div key={i._id} onClick={() => onSelectItem && onSelectItem(i)}
                style={{ display: "flex", alignItems: "flex-start", gap: 6, padding: "5px 6px", borderRadius: 6, cursor: "pointer", background: i._id === item._id ? "#f5f9ff" : "transparent", borderLeft: i._id === item._id ? "3px solid #1a1a2e" : "3px solid transparent" }}>
                <Checkbox checked={i.status === "complete"} indeterminate={i.status === "pending_review"} disabled={!canEdit}
                  onClick={(e) => e.stopPropagation()} onChange={(e) => toggle(i, e.target.checked)} />
                <span style={{ fontSize: 12.5, textDecoration: i.status === "complete" || i.status === "abandoned" ? "line-through" : "none", color: i.status === "complete" || i.status === "abandoned" ? "#999" : "#333" }}>
                  {i.title}
                  {i.dueDate && <span style={{ color: i.status !== "complete" && new Date(i.dueDate) < new Date() ? "#cf1322" : "#999", marginLeft: 6, fontSize: 11 }}>{String(i.dueDate).slice(0, 10)}</span>}
                </span>
              </div>
            ))}
            {!listItems.length && <div style={{ color: "#999", fontSize: 12 }}>No items</div>}
          </div>
        </div>

        {/* Right: the item */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "6px 16px", marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4 }}>Partner / contractor</div>
              <Select filterOption={matchOption} size="small" allowClear showSearch optionFilterProp="label" style={{ width: "100%" }} disabled={!canEdit}
                placeholder={item.contractorLabel ? `${item.contractorLabel} (label - not mapped yet)` : "Partner company"}
                value={item.responsibleCompanyId || undefined} options={companyOptions}
                onChange={(v) => patch({ responsibleCompanyId: v || "", responsibleUserId: "" })} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4 }}>Responsible person</div>
              <Select size="small" allowClear showSearch optionFilterProp="label" style={{ width: "100%" }} disabled={!canEdit || !item.responsibleCompanyId}
                placeholder={item.responsibleCompanyId ? "Person" : "Partner first"}
                value={item.responsibleUserId || undefined} options={peopleOf(item.responsibleCompanyId)}
                filterOption={filterPeopleOption} optionRender={renderPeopleOption}
                onChange={(v, opt) => patch({ responsibleUserId: v || "", responsibleUserName: opt?.name || "" })} />
              {item.responsibleUserName && !item.responsibleUserId && <div style={{ fontSize: 11, color: "#888" }}>{item.responsibleUserName}</div>}
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4 }}>Schedule activity</div>
              {canEdit ? (
                <ActivityPicker size="small" style={{ width: "100%", minWidth: 0 }} items={planItems}
                  placeholder="Link to a schedule activity or category" value={item.ganttItemId?._id || item.ganttItemId || undefined}
                  onChange={(v) => patch({ ganttItemId: v || "" })} />
              ) : <div style={{ fontSize: 13 }} title={activityTitle(item.ganttItemId)}>{activityLabel(item.ganttItemId) || "-"}</div>}
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4 }}>Due · assigned now</div>
              <div style={{ fontSize: 12.5 }}>
                {item.dueDate ? String(item.dueDate).slice(0, 10) : "—"}
                {item.currentAssignee?.email && <span style={{ color: "#666" }}> · {item.currentAssignee.name || item.currentAssignee.email}</span>}
                {company && <span style={{ color: "#666" }}> · {company.companyName}</span>}
              </div>
            </div>
          </div>
          {item.notes && <Typography.Paragraph style={{ fontSize: 12.5, whiteSpace: "pre-wrap", background: "#fafafa", padding: 8, borderRadius: 6 }}>{item.notes}</Typography.Paragraph>}
          {item.answer && <div style={{ fontSize: 12.5, marginBottom: 8 }}><b>Answer:</b> <span style={{ whiteSpace: "pre-wrap" }}>{item.answer}</span></div>}

          <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4, margin: "8px 0 4px" }}>Updates ({(item.thread || []).length})</div>
          <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: 6, padding: 8 }}>
            {(item.thread || []).length === 0 && <div style={{ color: "#999", fontSize: 12 }}>No updates yet.</div>}
            {(item.thread || []).map((t, i) => (
              <div key={t._id || i} style={{ padding: "6px 0", borderBottom: "1px solid #fafafa" }}>
                <div style={{ fontSize: 12 }}>
                  <b>{t.name}</b>
                  {t.status && <Tag style={{ marginLeft: 6 }} color={STATUS_COLOR[t.status] || "default"}>{STATUS_LABEL[t.status] || t.status}</Tag>}
                  <span style={{ color: "#999", marginLeft: 6 }}>{fmt(t.at)}</span>
                  {t.assignedToEmail && <span style={{ color: "#666", marginLeft: 6 }}>→ handed to {t.assignedToName || t.assignedToEmail}</span>}
                </div>
                {t.comment && <div style={{ fontSize: 12.5, whiteSpace: "pre-wrap", marginTop: 2 }}>{t.comment}</div>}
                <ChecklistThreadMedia attachments={t.attachments || []} screenshotUrl={t.screenshotUrl} />
              </div>
            ))}
            <div ref={threadEndRef} />
          </div>
          {(item.screenshots || []).length > 0 && (
            <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
              {(item.screenshots || []).map((s, i) => <a key={s._id || i} href={`${url}${s.url}`} target="_blank" rel="noreferrer"><img src={`${url}${s.url}`} alt="" style={{ width: 64, height: 48, objectFit: "cover", borderRadius: 4, border: "1px solid #eee" }} /></a>)}
            </div>
          )}

          <div style={{ marginTop: 12, border: "1px solid #f0f0f0", borderRadius: 6, padding: 10, background: "#fff" }}>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Post an update</div>
            {needsShot && (
              <div style={{ fontSize: 12, marginBottom: 6, color: hasAnyShot ? "#2e9e5b" : "#B84B0A" }}>
                {hasAnyShot ? "✓ Screenshot provided - this item can be completed." : "! This item requires a screenshot of the work before it can be marked complete."}
              </div>
            )}
            <Space wrap style={{ marginBottom: 6 }}>
              <Select size="small" value={status} onChange={setStatus} style={{ width: 150 }}
                options={[{ value: "open", label: "Pending" }, { value: "in_progress", label: "In progress" }, { value: "complete", label: canEdit ? "Complete" : "Complete (for review)" }]} />
            </Space>
            <div style={{ marginBottom: 6 }}>
              <ChecklistMediaPicker upload={uploadMedia} value={media} onChange={setMedia} pasteActive={open}
                hint={status === "complete" ? "optional proof of the completed check - photo, video, Excel, PDF, DWG · or paste (Ctrl/Cmd+V)" : undefined} />
            </div>
            <Input.TextArea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comment - what was checked, what's left, blockers…" />
            <Space wrap style={{ marginTop: 6, width: "100%" }}>
              <Select size="small" allowClear showSearch optionFilterProp="label" style={{ width: 300 }} placeholder="Hand to someone (they get the link by email)"
                value={assignTo} options={everyone} onChange={setAssignTo}
                filterOption={filterPeopleOption} optionRender={renderPeopleOption}
                onSearch={(t) => { if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) setAssignTo(t.toLowerCase()); }} />
              <Button type="primary" size="small" icon={<SendOutlined />} loading={posting} onClick={post}>Post update</Button>
            </Space>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
